import React, { useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, FreeMode } from 'swiper/modules';
import { buildClassName } from 'utils/build-class-name';
import ListingCard, { ListingCardData } from 'components/listing-card/listing-card';
import CTACard, { CTA_CARD_TYPE_AVAILABLE, CTA_CARD_TYPE_FEATURED, CTA_CARD_TYPE_SOLD, CtaCardType } from 'components/home-page/home-modules/cta-card';
import { listingCardIds } from 'constants/test-constants';
import styles from './style.module.scss';
import useSizeClass, { useIsMobile } from 'hooks/use-size-class';
import {
  GTM_CLICK_HOMEPAGE_SWIPER_SALE,
  GTM_CLICK_HOMEPAGE_SWIPER_SOLD,
  GTM_CLICK_HOMEPAGE_SWIPER_FEATURED,
  GTM_CLICK_HOMEPAGE_SALE,
  GTM_CLICK_HOMEPAGE_SOLD,
  GTM_CLICK_HOMEPAGE_FEATURED,
} from 'constants/events';
import CarouselArrows from 'components/swiper-carousel/components/arrows';
import LoadingSkeleton from 'components/loading-skeleton';
import { AnalyticsController } from 'analytics/analytics-controller';

import type { IUserLocation } from 'utils/user-location';
import type SwiperClass from 'swiper/types/swiper-class';
import type { OnListingCardClickProps } from 'utils/listing-card';
import type { ListingBedBathSqftLabelsData } from 'components/listing-card/listing-bed-bath-sqft-labels';

//#region Types

export type ListingCardComparableListing = ListingBedBathSqftLabelsData;

type ListingCardCarouselProps = {
  listingCardsData?: {
    listings: ListingCardData[];
    userLocation?: IUserLocation | null;
    ctaType?: CtaCardType;
    redirectUrl?: string;
  };
  listingToCompare?: ListingCardComparableListing;
  isCrawler?: boolean;
  gtmOnClickEvent?: string;
  isOnHomepage?: boolean;
  /** When false slides advance one at a time (default). When true: slides advance by page (skips the visible slides). */
  slidesPerGroupAuto?: boolean;
  onListingCardClick: (props: OnListingCardClickProps) => void;
};
//#endregion

//#region Constants
const DESKTOP_INDEX_LOAD_START = 2 as const;
const DESKTOP_INDEX_LOAD_END = 4;
const DESKTOP_INDEX_LARGE_LOAD_END = 7;
//#endregion

//#region Component
export default function ListingCardCarousel({
  listingCardsData,
  listingToCompare,
  isCrawler,
  gtmOnClickEvent,
  onListingCardClick,
  slidesPerGroupAuto = false,
}: ListingCardCarouselProps) {
  const { listings, ctaType, userLocation, redirectUrl } = listingCardsData || {};
  const [swiper, setSwiper] = useState<SwiperClass>();
  const [carouselItems, setCarouselItems] = useState<JSX.Element[]>([]);
  const [initialSlideChangeDone, setInitialSlideChangeDone] = useState(false);
  const [isLoopOn, setIsLoopOn] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const isMobile = useIsMobile();
  const isLargeDesktop = useSizeClass({ min: 1500 });
  const isDesktop = useSizeClass({ min: 1000 });
  const calculatedDesktopLoadEnd = isLargeDesktop ? DESKTOP_INDEX_LARGE_LOAD_END : DESKTOP_INDEX_LOAD_END;
  const listingsCount = (listings || []).length;
  // Use a unique key derived from listing ids, to force carousel to rerender when listings change
  // This is to fix teh stale items issue (cached lazy loaded slices) with the carousel.
  const listingsKey = useMemo(
    () => (listings ?? []).map(l => l.id).join(','),
    [listings]
  );

  useEffect(() => {
    setCarouselItems([]);
    setHasInitialized(false);
    setInitialSlideChangeDone(false);
    setIsLoopOn(false);
  }, [listingsKey]);
  const shouldLoopBeEnabled = (listings || []).length >= 3;
  const isNotFull = (listings || []).length <= 3 && isDesktop;

  const loadCarouselItems = (swiperInstance: SwiperClass | null) => {
    if (!hasInitialized && swiperInstance && listings && listingsCount > 0) {
      const sliceIndex = !isMobile ? calculatedDesktopLoadEnd : DESKTOP_INDEX_LOAD_START;
      if (listingsCount > sliceIndex) {
        const items = listings.slice(sliceIndex).map((listing, index) => (
          <SwiperSlide virtualIndex={index} key={listing.id} className={buildClassName(styles.card, 'class'+index)}>
            <ListingCard listing={listing} listingToCompare={listingToCompare} isCrawler={index < 2 && isCrawler} gtmOnClickEvent={gtmOnClickEvent} onListingCardClick={onListingCardClick} />
          </SwiperSlide>
        ));
        if (ctaType) {
          items.push(
            <SwiperSlide virtualIndex={items.length} key={items.length} className={styles.card}>
              <CTACard key={items.length} type={ctaType} userLocation={userLocation} redirectUrl={redirectUrl} />
            </SwiperSlide>
          );
        }
        setCarouselItems(items);
        setHasInitialized(true);
      }
    }
  };

  // Initialize carousel items after mount and when swiper is ready
  useEffect(() => {
    loadCarouselItems(swiper);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiper, listings, listingsCount, hasInitialized, isMobile, calculatedDesktopLoadEnd, ctaType, userLocation, redirectUrl]);

  const handleScrollEvents = () => {
    if (!initialSlideChangeDone) {
      setInitialSlideChangeDone(true);
    } else {
      if (ctaType === CTA_CARD_TYPE_AVAILABLE) {
        AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SWIPER_SALE);
      } else if (ctaType === CTA_CARD_TYPE_SOLD) {
        AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SWIPER_SOLD);
      } else if (ctaType === CTA_CARD_TYPE_FEATURED) {
        AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SWIPER_FEATURED);
      }
    }
  };

  const handleClickEvents = () => {
    if (ctaType === CTA_CARD_TYPE_AVAILABLE) {
      AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SALE);
    } else if (ctaType === CTA_CARD_TYPE_SOLD) {
      AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD);
    } else if (ctaType === CTA_CARD_TYPE_FEATURED) {
      AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_FEATURED);
    }
  };

  const slideTo = (slide: 'next' | 'prev' | number) => {
    if (shouldLoopBeEnabled && slide === 'prev' && swiper?.activeIndex === 0) {
      setIsLoopOn(true);
    }

    const timeout = shouldLoopBeEnabled && !isLoopOn ? 100 : 0;

    // Initial timeout to let slider know loop is on
    setTimeout(() => {
      if (slide === 'next') {
        swiper?.slideNext();
      } else if (slide === 'prev') {
        swiper?.slidePrev();
      }
    }, timeout);
  };

  const loadRestOfItems = (swiper: SwiperClass) => {
    loadCarouselItems(swiper);

    if (shouldLoopBeEnabled && swiper && (swiper.isBeginning || swiper.isEnd)) {
      setTimeout(() => setIsLoopOn(true), 600);
    }
  };

  if (!listings || listingsCount === 0) {
    return (
      <div className={styles.component} data-testid={listingCardIds.listingsSwiper}>
        <div className={styles.slider}>
          <LoadingSkeleton className={styles.card} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.component} data-testid={listingCardIds.listingsSwiper}>
      <Swiper
        onSwiper={setSwiper}
        className={buildClassName(styles.slider)}
        loop={isLoopOn}
        slidesPerView={'auto'}
        {...(slidesPerGroupAuto ? { slidesPerGroupAuto: true } : {})}
        spaceBetween={20}
        onClick={handleClickEvents}
        onSlideChangeTransitionEnd={handleScrollEvents}
        onSlideChangeTransitionStart={loadRestOfItems}
        onTouchStart={loadRestOfItems}
        onScroll={(swiper: SwiperClass) => { loadRestOfItems(swiper); setIsLoopOn(true); }}
        freeMode={isNotFull ? false : true}
        mousewheel={{
          releaseOnEdges: true,
          forceToAxis: true,
        }}
        modules={[Mousewheel, FreeMode]}
      >
        {listings && !!listingsCount &&
          <SwiperSlide virtualIndex={0} key={listings[0].id} className={styles.card}>
            <ListingCard listing={listings[0]} listingToCompare={listingToCompare} isImageVisible={true} isCrawler={isCrawler} gtmOnClickEvent={gtmOnClickEvent} onListingCardClick={onListingCardClick} />
          </SwiperSlide>
        }
        {listings && listingsCount > 1 &&
          <SwiperSlide virtualIndex={1} key={listings[1].id} className={styles.card}>
            <ListingCard listing={listings[1]} listingToCompare={listingToCompare} isCrawler={isCrawler} gtmOnClickEvent={gtmOnClickEvent} onListingCardClick={onListingCardClick} />
          </SwiperSlide>
        }
        {!isMobile && (listings || []).slice(DESKTOP_INDEX_LOAD_START, calculatedDesktopLoadEnd).map((listing, index) => (
          <SwiperSlide virtualIndex={index} key={listing.id} className={styles.card}>
            <ListingCard listing={listing} listingToCompare={listingToCompare} isCrawler={index < 2 && isCrawler} gtmOnClickEvent={gtmOnClickEvent} onListingCardClick={onListingCardClick} />
          </SwiperSlide>
        ))}
        {carouselItems}
      </Swiper>
      {!isNotFull && <CarouselArrows isLightbox={false} isListingCardCarousel={true} slideTo={slideTo} className={styles.arrow} />}
    </div>
  );
}
//#endregion